import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

// Connects to data-controller="file-drop"
// Drag-and-drop direct uploads for documents. Fully reusable — works for any
// documentable parent by accepting either an endpoint URL for persisted records
// or a nested attachment field name for a parent form.
export default class extends Controller {
  static targets = ["overlay", "fileInput", "attachmentFields", "attachedFile", "attachmentList"]

  static values = {
    attachmentName: String,
    url: String,
    frameId: { type: String, default: "documents_list" },
    paramName: { type: String, default: "document" },
    fileField: { type: String, default: "file" }
  }

  connect() {
    this.dragCounter = 0
    this.activeUploads = 0
    this.submitControlStates = new Map()
    this.renderAttachedFiles()
  }

  dragEnter(event) {
    event.preventDefault()
    if (!this.isFileDrag(event)) return

    this.dragCounter++
    if (this.dragCounter === 1) {
      this.showOverlay()
    }
  }

  dragOver(event) {
    if (!this.isFileDrag(event)) return
    event.preventDefault()
  }

  dragLeave(event) {
    event.preventDefault()
    if (!this.isFileDrag(event)) return

    this.dragCounter--
    if (this.dragCounter === 0) {
      this.hideOverlay()
    }
  }

  drop(event) {
    if (!this.isFileDrag(event)) return
    event.preventDefault()
    this.dragCounter = 0
    this.hideOverlay()

    const files = Array.from(event.dataTransfer.files)
    if (files.length === 0) return

    this.uploadFiles(files)
  }

  isFileDrag(event) {
    return event.dataTransfer?.types?.includes("Files")
  }

  browse() {
    this.fileInputTarget.click()
  }

  fileSelected() {
    const files = Array.from(this.fileInputTarget.files)
    if (files.length === 0) return

    this.uploadFiles(files)
    this.fileInputTarget.value = ""
  }

  async uploadFiles(files) {
    const total = files.length
    let completed = 0
    let failed = 0

    this.beginUploads(total)
    this.showProgress(completed, total)

    const uploads = files.map(async (file) => {
      try {
        await this.uploadFile(file)
      } catch {
        failed++
      } finally {
        completed++
        this.finishUpload()
        this.showProgress(completed, total)
      }
    })

    await Promise.all(uploads)
    this.hideOverlay()

    const succeeded = total - failed

    if (succeeded > 0) {
      if (this.hasUrlValue) this.reloadFrame()
      this.showToast("success", `${succeeded} document${succeeded === 1 ? "" : "s"} uploaded`)
    }

    if (failed > 0) {
      this.showToast("error", `${failed} document${failed === 1 ? "" : "s"} failed to upload`)
    }
  }

  async uploadFile(file) {
    const signedId = await this.directUpload(file)

    if (this.hasAttachmentNameValue) {
      this.appendAttachment(signedId, file.name)
      return
    }

    const response = await fetch(this.urlValue, {
      method: "POST",
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken
      },
      body: JSON.stringify({
        [this.paramNameValue]: { [this.fileFieldValue]: signedId }
      })
    })

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status}`)
    }

    return response.json()
  }

  appendAttachment(signedId, fileName) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = this.attachmentNameValue
    input.value = signedId
    input.dataset.fileDropTarget = "attachedFile"
    input.dataset.fileName = fileName
    this.attachmentFieldsTarget.append(input)
    this.renderAttachedFiles()
  }

  renderAttachedFiles() {
    if (!this.hasAttachmentListTarget) return

    const fileNames = this.attachedFileTargets.map((input) => input.dataset.fileName)
    this.attachmentListTarget.textContent = fileNames.length === 0
      ? "No documents uploaded."
      : `${fileNames.length} document${fileNames.length === 1 ? "" : "s"} uploaded: ${fileNames.join(", ")}`
  }

  directUpload(file) {
    const url = "/rails/active_storage/direct_uploads"
    return new Promise((resolve, reject) => {
      const upload = new DirectUpload(file, url)
      upload.create((error, blob) => {
        if (error) {
          reject(error)
        } else {
          resolve(blob.signed_id)
        }
      })
    })
  }

  beginUploads(count) {
    if (!this.hasAttachmentNameValue) return

    if (this.activeUploads === 0) {
      this.submitControls.forEach((control) => {
        this.submitControlStates.set(control, control.disabled)
        control.disabled = true
      })
    }
    this.activeUploads += count
  }

  finishUpload() {
    if (!this.hasAttachmentNameValue) return

    this.activeUploads--
    if (this.activeUploads > 0) return

    this.submitControlStates.forEach((wasDisabled, control) => {
      control.disabled = wasDisabled
    })
    this.submitControlStates.clear()
  }

  get submitControls() {
    const form = this.element.closest("form")
    if (!form) return []

    return Array.from(form.querySelectorAll('button[type="submit"], input[type="submit"]'))
  }

  showOverlay() {
    if (!this.hasOverlayTarget) return
    this.overlayTarget.classList.remove("hidden")
  }

  hideOverlay() {
    if (!this.hasOverlayTarget) return
    this.overlayTarget.classList.add("hidden")
  }

  showProgress(completed, total) {
    if (!this.hasOverlayTarget) return
    this.overlayTarget.classList.remove("hidden")
    const label = this.overlayTarget.querySelector("[data-role='label']")
    if (label) {
      label.textContent = (completed < total)
        ? `Uploading ${completed + 1} of ${total}...`
        : "Processing..."
    }
  }

  reloadFrame() {
    const frame = document.getElementById(this.frameIdValue)
    if (frame?.reload) {
      frame.reload()
    }
  }

  showToast(type, message) {
    document.dispatchEvent(new CustomEvent("toast:show", {
      detail: { type, message }
    }))
  }

  get csrfToken() {
    const meta = document.querySelector("meta[name='csrf-token']")
    return meta?.content || ""
  }
}
